import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Progress, Spin, Tooltip } from "antd";
import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "../../../services/axios";
import "./style.scss";

export type ThermalBatteryStatus = {
  ok: boolean;
  device_id?: string;
  soc_pct?: number;
  e_now_kwh?: number;
  e_free_kwh?: number;
  c_useful_kwh?: number;
  c_max_kwh?: number;
  e_min_kwh?: number;
  t_min_c?: number;
  t_max_eff_c?: number;
  water_temperature_c?: number;
  p_charge_100_w?: number;
  t_remaining_h?: number;
  t_remaining_note?: string;
  useful_overheat_days_count?: number;
  recent_solar_days_count?: number;
  useful_window_days?: number;
  historical_qualifying_days_count?: number;
  daily_max_temp_fallback_count?: number | null;
  overheat_detection_method?: string;
  t_overheat_empirical_c?: number | null;
  saturation_days_count?: number;
  saturation_buckets_count?: number;
  today_temp_delta_c?: number | null;
  selection_rule?: string;
  computed_at?: string;
  error?: string;
  detail?: string;
};

const ThermalBatteryView: React.FC = () => {
  const { device_id } = useParams<{ device_id: string }>();
  const { t } = useTranslation("settings");
  const [data, setData] = useState<ThermalBatteryStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
      if (!device_id) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await axios.get<ThermalBatteryStatus>(
          `/api-v1/device/${device_id}/battery-status/`,
          { timeout: 120000 }
        );
        setData(resp.data);
        if (!resp.data.ok) {
          setError(resp.data.detail || resp.data.error || t("thermal_battery_error"));
        }
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { detail?: string } }; message?: string };
        const msg =
          ax.response?.data?.detail ||
          ax.message ||
          t("thermal_battery_error");
        setError(msg);
        setData(null);
      } finally {
        setLoading(false);
      }
    }, [device_id, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return (
    <div className="thermal-battery-view">
      <div className="thermal-battery-toolbar">
        <p className="thermal-battery-description">{t("thermal_battery_description")}</p>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchStatus()}
          loading={loading}
          type="default"
        >
          {t("thermal_battery_refresh")}
        </Button>
      </div>

      {error && (
        <Alert
          type="warning"
          showIcon
          message={t("thermal_battery_error")}
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading && !data?.ok && (
        <div className="thermal-battery-loading">
          <Spin size="large" />
          <span>{t("thermal_battery_loading")}</span>
        </div>
      )}

      {data?.ok && (
        <>
          <Card className="thermal-battery-soc-card" title={t("thermal_battery_soc_title")}>
            <Progress
              type="dashboard"
              percent={data.soc_pct ?? 0}
              format={(pct) => `${pct}%`}
              strokeColor="#72A363"
            />
            <div className="thermal-battery-metrics">
              <Metric
                label={t("thermal_battery_free")}
                value={`${data.e_free_kwh ?? "—"} kWh`}
              />
              <Metric
                label={t("thermal_battery_stored")}
                value={`${data.e_now_kwh ?? "—"} kWh`}
              />
              <Metric
                label={t("thermal_battery_temperature")}
                value={`${data.water_temperature_c ?? "—"} °C`}
              />
            </div>
          </Card>

          <Card title={t("thermal_battery_capacity_title")} className="thermal-battery-card">
            <div className="thermal-battery-grid">
              <Metric
                label={t("thermal_battery_useful")}
                value={`${data.c_useful_kwh ?? "—"} kWh`}
              />
              <Metric
                label={t("thermal_battery_max")}
                value={`${data.c_max_kwh ?? "—"} kWh`}
              />
              <Metric label={t("thermal_battery_min")} value={`${data.e_min_kwh ?? 0} kWh`} />
              <Metric
                label={t("thermal_battery_valid_days")}
                value={String(data.useful_overheat_days_count ?? "—")}
              />
              <Metric
                label={t("thermal_battery_recent_solar_days")}
                value={String(data.recent_solar_days_count ?? "—")}
              />
              {data.overheat_detection_method && (
                <Metric
                  label={t("thermal_battery_detection_method")}
                  value={t(
                    `thermal_battery_detection_${data.overheat_detection_method}`,
                    { defaultValue: data.overheat_detection_method }
                  )}
                />
              )}
              {data.overheat_detection_method === "historical" &&
                data.saturation_days_count != null && (
                  <Metric
                    label={t("thermal_battery_saturation_days")}
                    value={String(data.saturation_days_count)}
                  />
                )}
              {data.overheat_detection_method === "daily_max_temp_fallback" &&
                data.historical_qualifying_days_count != null && (
                  <Metric
                    label={t("thermal_battery_historical_days_attempted")}
                    value={String(data.historical_qualifying_days_count)}
                  />
                )}
              {data.t_overheat_empirical_c != null && (
                <Metric
                  label={t("thermal_battery_t_overheat_empirical")}
                  value={`${data.t_overheat_empirical_c} °C`}
                />
              )}
            </div>
          </Card>

          <Card title={t("thermal_battery_charging_title")} className="thermal-battery-card">
            <div className="thermal-battery-grid">
              <Metric
                label={t("thermal_battery_charge_power")}
                value={`${data.p_charge_100_w ?? "—"} W`}
              />
              <Metric
                label={
                  <span>
                    {t("thermal_battery_time_remaining")}
                    <Tooltip title={data.t_remaining_note}>
                      <InfoCircleOutlined style={{ marginLeft: 6, opacity: 0.45 }} />
                    </Tooltip>
                  </span>
                }
                value={
                  data.t_remaining_h != null
                    ? `~${data.t_remaining_h} ${t("thermal_battery_hours")}`
                    : "—"
                }
              />
              <Metric
                label={t("thermal_battery_t_range")}
                value={`${data.t_min_c ?? "—"} – ${data.t_max_eff_c ?? "—"} °C`}
              />
              {data.today_temp_delta_c != null && (
                <Metric
                  label={t("thermal_battery_today_delta")}
                  value={`${data.today_temp_delta_c} °C`}
                />
              )}
            </div>
          </Card>

          {data.selection_rule && (
            <p className="thermal-battery-selection-rule">{data.selection_rule}</p>
          )}

          {data.computed_at && (
            <p className="thermal-battery-computed-at">
              {t("thermal_battery_computed_at")}:{" "}
              {new Date(data.computed_at).toLocaleString()}
            </p>
          )}
        </>
      )}
    </div>
  );
};

const Metric = ({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => (
  <div className="thermal-battery-metric">
    <span className="label">{label}</span>
    <span className="value">{value}</span>
  </div>
);

export default ThermalBatteryView;
